import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataTable, DropdownMenu, TagColored, TagFeedback, Text, TooltipTrigger, Button, Spinner, SparkIcon, CardDefault, Search, sparkToast, ReadOnlyGuard } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { withSelectedThresholdsOverride } from '@/features/evaluation/utils/modelResponsesThresholdOverride';
import { getPredictionColor } from '@/shared/utils/helpers';
import { SubTaskType, TaskType } from '@/shared/domain/taskTypes';
import LabelSelector from '@/features/tasks/components/LabelSelector';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import ReviewStatusIndicator from '@/features/datasets/components/DataSelection/ReviewStatusIndicator';
import { useExport } from '@/shared/hooks/useExport';
import { ALL_LABELS, NONE_SELECTED } from '@/features/datasets/components/DataSelection/RawRecordsStatus/constants';
import EntityExtractionsTable, { EE_RENDER_MODES } from './EntityExtractionsTable';
import './ModelResponsesTable.css';

const SpecificAILogo = ({ style = {}, size = '18px' }) => (
    <img src="/images/brandmark_black.svg" alt="SpecificAI" style={{ width: size, height: size, marginRight: '4px', verticalAlign: 'middle', ...style }} />
);

const ErrorDetailsAlert = ({ title, message, details, type = 'error', showIcon = true, style }) => {
    const [open, setOpen] = React.useState(false);
    const detailsText = React.useMemo(() => {
        if (details == null) return '';
        if (typeof details === 'string') return details;
        try { return JSON.stringify(details, null, 2); } catch { return String(details); }
    }, [details]);
    const hasDetails = Boolean(detailsText && detailsText.trim().length > 0);
    return (
        <>
            <div style={{ padding: '12px 16px', borderRadius: 8, border: '1px solid', borderColor: type === 'error' ? '#ffa39e' : '#ffe58f', background: type === 'error' ? '#fff1f0' : '#fffbe6', ...style }}>
                <div style={{ fontWeight: 500 }}>{message}</div>
                {hasDetails && <button type="button" onClick={() => setOpen(true)} style={{ background: 'none', border: 'none', color: '#1677ff', cursor: 'pointer', padding: 0, marginTop: 4 }}>View error details</button>}
            </div>
            {hasDetails && open && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)' }} onClick={() => setOpen(false)}>
                    <div style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 900, width: '90%', maxHeight: '80vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ fontWeight: 600, marginBottom: 12 }}>{title || 'Error details'}</div>
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{detailsText}</pre>
                    </div>
                </div>
            )}
        </>
    );
};
import LabelPredictionStatusIcon from '@/features/datasets/components/LabelPredictionStatusIcon';

// Same copy as the Fallback label option in task setup. Shown when confidence
// rejection emptied the prediction (all scores below threshold). Distinct from
// NONE_SELECTED (multilabel training sentinel) and from "No Response" (the
// model produced no output at all).
const NO_PREDICTIONS_LABEL = 'No predictions';
const LOW_CONFIDENCE_NO_PREDICTIONS_TOOLTIP =
    "the model didn't select a label because all predictions had low confidence";

const LowConfidenceNoPredictions = ({ expectedLabels }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <LabelPredictionStatusIcon actual={null} expected={expectedLabels} />
        <TagColored label={NO_PREDICTIONS_LABEL} variant="disabled" />
        <TooltipTrigger text={LOW_CONFIDENCE_NO_PREDICTIONS_TOOLTIP}>
            <span className="mrt-no-predictions-info">
                <SparkIcon slug="circle-info" size="small" />
            </span>
        </TooltipTrigger>
    </div>
);

// Column-header model name that ellipsizes when space runs out and shows the
// full name in a tooltip on hover — but only when the text is actually cut.
const HeaderModelName = ({ icon, name }) => {
    const textRef = useRef(null);
    const [truncated, setTruncated] = useState(false);

    // Measured on hover so it always reflects the current column width
    // without needing a resize observer.
    const checkTruncation = () => {
        const el = textRef.current;
        if (el) setTruncated(el.scrollWidth > el.clientWidth);
    };

    return (
        <TooltipTrigger text={name} disabled={!truncated} className="mrt-header-model-name-trigger">
            <span className="mrt-header-model-name" onMouseEnter={checkTruncation}>
                {icon}
                <span ref={textRef} className="mrt-header-model-name-text">{name}</span>
            </span>
        </TooltipTrigger>
    );
};



const ModelResponsesTable = ({
    llmUsecaseId,
    version,
    /**
     * When provided, locks the /model-responses query to this specific distillation
     * event (i.e. a benchmark row picked in the Model Details drawer's Benchmarks tab).
     */
    selectedDistillationEventId = null,
    currentUsecase,
    displayNames,
    dropdownLabelRender,
    teacherModelProcessing = false,
    teacherModelFailed = false,
    teacherModelErrorDetails = null,
    hideTeacherFailedAlert = false,
    studentModelProcessing = false,
    compareWith = 'oldModel',
    comparisonModelName,
    metadata,
    hasBenchmark = false,
    evaluationDatasets = [],
    evaluationBenchmarks = [],
    selectedLabel = ALL_LABELS,
    selectedLanguage = null,
    taskType,
    sampleCounters = {},
    activeTab,
    relationTypes,
    selectedThresholds = null,
    /**
     * Controls which slice of the NER entity-extractions UI to render. Only relevant
     * when `taskType === NER`. One of `'all' | 'overview' | 'samples'`.
     * - 'all'      (default): the legacy collapsible card with both pies + samples table.
     * - 'overview': just the two pie charts, no collapsible wrapper.
     * - 'samples':  just the search bar + sample comparison table, no collapsible wrapper.
     */
    entityExtractionsMode = EE_RENDER_MODES.ALL,
}) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total, range) => total > 0 ? `${range[0]}-${range[1]} of ${total} items` : `0 items`,
    });

    // Export hook
    const { exportData } = useExport();

    // Add a ref to track if this is the initial load
    const isInitialLoad = useRef(true);

    // Add a ref to track which filter combination we're trying
    const filterAttempt = useRef(0);

    const defaultFilter = taskType === TaskType.NER ? {} : {
        [compareWith === 'oldModel' ? 'oldModel' : 'comparisonModel']: 'miss',
        newModel: 'miss',
    }

    const [filters, setFilters] = useState({
        ...defaultFilter,
        testResponse: selectedLabel !== ALL_LABELS ? selectedLabel : null,
        example: '',
    });
    const [uniqueLabels, setUniqueLabels] = useState([]);
    const [loadingLabels, setLoadingLabels] = useState(false);
    const [allEntities, setAllEntities] = useState([]);
    // Row currently in true-labels edit mode (entered via the hover pencil button)
    const [editingLabelsKey, setEditingLabelsKey] = useState(null);

    const comparisonType = compareWith === 'file' ? 'results_file'
        : compareWith === 'oldModel' ? 'teacher'
            : 'version';
    // Determine if we're comparing with a version instead of the teacher model
    const isVersionComparison = compareWith && comparisonType === 'version';

    // Extract the comparison version number if comparing with another SpecificAI version
    const comparisonVersion = isVersionComparison ? compareWith : null;

    const fetchData = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            // Create a filters object that only includes non-null/empty values
            const apiFilters = Object.entries(filters).reduce((acc, [key, value]) => {
                if (value !== null && value !== '') {
                    // Special handling for testResponse filter
                    if (key === 'testResponse') {
                        if (value !== NONE_SELECTED && value !== ALL_LABELS) {
                            key = taskType === TaskType.classification ? 'test_response_label' : 'test_response_entity';
                            acc[key] = value;
                        }
                    } else {
                        acc[key] = value;
                    }
                }
                return acc;
            }, {});
            if (selectedLanguage) {
                apiFilters.language = selectedLanguage;
            }

            const requestData = {
                client_id: currentUsecase?.client_id,
                llm_usecase_id: llmUsecaseId,
                version: version,
                page: page - 1,
                page_size: pageSize,
                filters: apiFilters,
                // Send sub_task_type only for NER_WITH_RELATIONS
                sub_task_type: (taskType == TaskType.NER_WITH_RELATIONS && activeTab == 'entities')
                    ? SubTaskType.Ner
                    : (taskType == TaskType.NER_WITH_RELATIONS && activeTab == 'relations')
                        ? SubTaskType.Relations
                        : null,
                selected_distillation_event_id: selectedDistillationEventId || null,
            };

            // Add comparison version if comparing with another version
            if (isVersionComparison) {
                requestData.comparison_version = comparisonVersion;
                requestData.is_version_comparison = true;
            }

            if (comparisonType === 'results_file') {
                requestData.is_file_comparison = true;
            }

            const payload = withSelectedThresholdsOverride(requestData, selectedThresholds);

            await data_from_server_post('model-responses', payload,
                (response) => {
                    if (response.success) {
                        setData(response.data);
                        setPagination({
                            ...pagination,
                            total: response.total,
                            current: page,
                            pageSize: pageSize,
                        });

                        // Only auto-adjust filters on initial load when no results
                        if (isInitialLoad.current && response.total === 0) {
                            switch (filterAttempt.current) {
                                case 0:
                                    // Starting state: both "incorrect" yielded no results
                                    // Try comparison "correct" and new "incorrect"
                                    filterAttempt.current = 1;
                                    setFilters(prev => ({
                                        ...prev,
                                        [comparisonFilterKey]: 'hit',
                                        newModel: 'miss'
                                    }));
                                    break;

                                case 1:
                                    // Second attempt: comparison "correct" and new "incorrect" yielded no results
                                    // Try comparison "incorrect" and new "correct"
                                    filterAttempt.current = 2;
                                    setFilters(prev => ({
                                        ...prev,
                                        [comparisonFilterKey]: 'miss',
                                        newModel: 'hit'
                                    }));
                                    break;

                                case 2:
                                    // Third attempt: comparison "incorrect" and new "correct" yielded no results
                                    // Clear all filters
                                    filterAttempt.current = 3;
                                    const { testResponse, example } = filters;
                                    setFilters({
                                        testResponse,
                                        example,
                                        [comparisonFilterKey]: null,
                                        newModel: null
                                    });
                                    break;

                                default:
                                    // We've tried all combinations, stop attempting
                                    isInitialLoad.current = false;
                                    filterAttempt.current = 0;
                            }
                        } else if (response.total > 0) {
                            // We found results, stop attempting
                            isInitialLoad.current = false;
                            filterAttempt.current = 0;
                        }
                    }
                }
            )
        } finally {
            setLoading(false);
        }
    };

    // New function to fetch all unique labels while respecting other filters
    const fetchAllUniqueLabels = async () => {
        setLoadingLabels(true);
        try {
            // Create a filters object that only includes non-null/empty values
            // but exclude the testResponse filter since we're fetching all possible labels
            const apiFilters = Object.entries(filters).reduce((acc, [key, value]) => {
                if (value !== null && value !== '' && key !== 'testResponse') {
                    acc[key] = value;
                }
                return acc;
            }, {});

            const requestData = {
                client_id: currentUsecase?.client_id,
                llm_usecase_id: llmUsecaseId,
                version: version,
                page: 0,  // Start at first page
                page_size: 1000,  // Request a large number to get as many unique labels as possible
                filters: apiFilters,
            };

            // Add comparison version if comparing with another version
            if (isVersionComparison) {
                requestData.comparison_version = comparisonVersion;
                requestData.is_version_comparison = true;
            }

            data_from_server_post("get-labels",
                llmUsecaseId
                ,
                (response) => {
                    setUniqueLabels(response?.label_names?.sort()); // Sort the labels alphabetically
                    setAllEntities(response?.entity_names?.sort());
                }
            )
        } finally {
            setLoadingLabels(false);
        }
    };

    useEffect(() => {
        fetchData(pagination.current, pagination.pageSize);
    }, [llmUsecaseId, version, filters, compareWith, comparisonVersion, selectedLanguage, selectedThresholds, selectedDistillationEventId]);

    // Reset pagination + initial-load heuristics when language changes
    useEffect(() => {
        isInitialLoad.current = true;
        filterAttempt.current = 0;
        setPagination(prev => ({
            ...prev,
            current: 1,
        }));
    }, [selectedLanguage]);

    // Update testResponse filter when selectedLabel changes
    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            testResponse: selectedLabel !== ALL_LABELS ? selectedLabel : null
        }));
        // Reset to first page when changing filters
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    }, [selectedLabel]);

    // Separate effect to fetch all unique labels when filters change (except testResponse filter)
    useEffect(() => {
        fetchAllUniqueLabels();
    }, [
        llmUsecaseId,
        version,
        filters.oldModel,
        filters.newModel,
        filters.example,
        filters.validity,
        compareWith,
        comparisonVersion
    ]);

    const handleTableChange = (newPagination) => {
        fetchData(newPagination.current, newPagination.pageSize);
    };

    const ResponseStatus = LabelPredictionStatusIcon;

    // Handle example text filter change
    const handleExampleFilterChange = (e) => {
        const value = e.target.value;
        setFilters({
            ...filters,
            example: value
        });
        // Reset to first page when changing filters
        setPagination({
            ...pagination,
            current: 1
        });
    };

    // Helper function to create a styled display name badge
    const renderDisplayNameBadge = (labelValue) => {
        if (!labelValue || !displayNames[labelValue] || displayNames[labelValue] === labelValue) {
            return null;
        }

        // Pick a color based on the label - we'll use consistent colors similar to the screenshot
        let bgColor = getPredictionColor(labelValue, uniqueLabels) || '#f97316'; // Default orange for "Numeric"

        return (
            <div style={{
                background: bgColor,
                color: 'white',
                fontSize: '14px',
                padding: '4px 16px',
                borderRadius: '16px',
                boxShadow: `0 2px 8px ${bgColor}70`,
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: '1.2',
                fontWeight: 'bold',
                marginLeft: '12px'
            }}>
                {displayNames[labelValue]}
            </div>
        );
    };

    // Render the comparison model response (teacher or other version)
    const renderComparisonResponse = (_, record) => {
        if (teacherModelFailed && !isVersionComparison) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <TagFeedback label={`🧠 ${comparisonModelName} Failed`} variant="error" />
                </div>
            );
        }
        // Handle processing states first
        if (teacherModelProcessing && !isVersionComparison) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <TagFeedback label={`🧠 ${comparisonModelName} Processing`} variant="info" showIcon />
                </div>
            );
        }

        if (isVersionComparison && teacherModelProcessing) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Spinner size="small" />
                    <TagFeedback label={`${comparisonModelName} Processing`} variant="info" />
                </div>
            );
        }

        // Get the appropriate responses based on comparison type
        const responses = isVersionComparison
            ? record.comparison_model_response
            : record.teacher_response;

        // Handle case where test_response is null or undefined
        const testLabels = (record.test_response || []).map(t => t.label);

        // Handle case where responses is null or undefined
        if (!responses) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <ResponseStatus actual={null} expected={testLabels} />
                    <TagColored label="No Response" color="grey" />
                </div>
            );
        }

        if (responses.length === 0) {
            // Empty in the DB record means the model produced no output at all,
            // whereas an empty array from threshold filtering is "No predictions".
            // The DB-empty check is done in the backend.
            const emptyInDb = isVersionComparison
                ? record.comparison_model_response_empty_in_db
                : record.teacher_response_empty_in_db;
            if (emptyInDb) {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <ResponseStatus actual={null} expected={testLabels} />
                        <TagColored label="No Response" color="grey" />
                    </div>
                );
            }
            return <LowConfidenceNoPredictions expectedLabels={testLabels} />;
        }

        // Render each response individually based on its validity
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                {responses?.map((response, index) => {
                    const label = typeof response === 'object' && response !== null ? response.label : response;
                    const isValid = response?.is_valid_label !== false;

                    // Skip empty labels
                    if (!label || label === '') {
                        return null;
                    }

                    // If the response is invalid
                    if (!isValid) {
                        // If the invalid filter is active, show the invalid label directly
                        if (filters.validity === 'invalid') {
                            return (
                                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ display: 'inline-flex', color: 'var(--semantic-feedback-warning-on-surface)' }}>
                                        <SparkIcon slug="circle-alert" size="small" />
                                    </span>
                                    <Text variant="base-regular">{label}</Text>
                                </div>
                            );
                        } else {
                            // Show "Invalid Label" tag with the actual label in tooltip
                            return (
                                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                    <TooltipTrigger text={label}>
                                        <span>
                                            <TagFeedback label="Invalid Label" variant="warning" showIcon />
                                        </span>
                                    </TooltipTrigger>
                                </div>
                            );
                        }
                    }

                    // If the response is valid, show it normally
                    return (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            <ResponseStatus actual={label} expected={testLabels} />
                            <Text variant="base-medium" color="secondary">{label}</Text>
                            {renderDisplayNameBadge(label)}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Render the new model response with display name if available
    const renderCurrentModelResponse = (_, record) => {
        // Handle processing state
        if (studentModelProcessing) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Spinner size="small" />
                    <SpecificAILogo size="14px" />
                    <TagFeedback label="Processing" variant="info" />
                </div>
            );
        }

        // Handle case where test_response is null or undefined
        const testLabels = (record.test_response || []).map(t => t.label);

        // Handle case where new_model_response is null or undefined
        if (!record?.new_model_response) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <ResponseStatus actual={null} expected={testLabels} />
                    <TagColored label="No Response" color="grey" />
                </div>
            );
        }

        // Convert text to array and extract labels
        const responses = record.new_model_response;
        if (taskType === TaskType.classification && !responses?.map) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <ResponseStatus actual={null} expected={testLabels} />
                    <TagColored label="No Response" color="grey" />
                </div>
            );
        }
        const labels = responses?.map(response => {
            // Handle case where response is an object with label property
            if (typeof response === 'object' && response !== null) {
                return response.label || '';
            }
            // Handle case where response is a string
            return response || '';
        }).filter(label => label !== ''); // Filter out empty labels

        if (responses.length === 0) {
            // Only surface "No Response" when the DB record itself is empty;
            // threshold-filtered empties read as "No predictions".
            if (record.new_model_response_empty_in_db) {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <ResponseStatus actual={null} expected={testLabels} />
                        <TagColored label="No Response" color="grey" />
                    </div>
                );
            }
            return <LowConfidenceNoPredictions expectedLabels={testLabels} />;
        }
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                {labels.map((label, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <ResponseStatus actual={label} expected={testLabels} />
                        <Text variant="base-medium" color="secondary">{label}</Text>
                        {renderDisplayNameBadge(label)}
                    </div>
                ))}
            </div>
        );
    };

    const handleLabelUpdate = useCallback((record, newLabels, is_benchmark = false) => {
        // The dataset name is only a hint; the backend resolves the example
        // across all current datasets and benchmarks of the task.
        data_from_server_post(
            "single-example/update-response",
            {
                client_id: currentUsecase?.client_id,
                dataset: is_benchmark ? evaluationBenchmarks?.[0] : evaluationDatasets?.[0],
                is_benchmark: is_benchmark,
                new_response: { labels: newLabels },
                is_document_reviewed: true,
                request_example: record.request_example,
                llm_usecase_id: currentUsecase?._id,
                model_responses_id: record.id,
            },
            (result) => {
                if (result?.success) {
                    sparkToast.success('Labels updated successfully!');
                    updateRecord(result.model_responses_record);
                    // We update without refetching the data because of performance issue in the endpoint 
                    setData(prev => prev.map(item =>
                        item.request_example === record.request_example ?
                            { ...item, test_response: newLabels.map(label => ({ label, is_valid_label: true })) }
                            : item
                    ));
                } else {
                    sparkToast.error('Failed to update labels');
                }
            }
        );
    }, [evaluationBenchmarks, evaluationDatasets, currentUsecase, setData]);

    // Function to update a record in the data array
    const updateRecord = useCallback((newRecord) => {
        setData(prevData =>
            prevData.map(item => {
                const idToCompare = newRecord.id || newRecord._id;
                return item.id === idToCompare ? { ...item, test_response: newRecord.test_response, is_validated: newRecord.is_validated } : item
            })
        );
    }, []);

    // True Labels cell: read-only colored tags by default; a pencil button
    // (visible on row hover) switches the cell to the label editor.
    const renderTestResponse = (text, record, hasBenchmark = false) => {
        if (taskType === TaskType.classification && !record.test_response?.map) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <TagColored label="No Response" variant="disabled" />
                </div>
            );
        }
        let labels = record.test_response.map(r => r.label)
        if (labels.length === 0) {
            labels = [NONE_SELECTED]
        } else {
            labels = labels.filter(label => label !== NONE_SELECTED)
        }

        const rowId = record.id ?? record.request_example;
        if (editingLabelsKey === rowId) {
            return (
                <div className="mrt-true-labels-cell">
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <ReadOnlyGuard>
                            <LabelSelector
                                labels={labels}
                                onSave={(newLabels) => {
                                    handleLabelUpdate(record, newLabels, hasBenchmark);
                                    setEditingLabelsKey(null);
                                }}
                                allLabelOptions={uniqueLabels}
                                taskType={currentUsecase?.task_type}
                                isMultilabel={currentUsecase?.is_multilabel || false}
                                record={record}
                            />
                        </ReadOnlyGuard>
                    </div>
                    <Button
                        size="tiny"
                        variant="ghost"
                        startIcon={<SparkIcon slug="close" />}
                        aria-label="Cancel editing"
                        iconSlug="close"
                        onClick={() => setEditingLabelsKey(null)}
                    />
                </div>
            );
        }

        return (
            <div className="mrt-true-labels-cell">
                <div className="mrt-true-labels-tags">
                    {labels.map((label) => (
                        <TagColored
                            key={label}
                            label={label}
                            variant={label === NONE_SELECTED ? 'disabled' : getSparkTagVariant(label, uniqueLabels)}
                        />
                    ))}
                </div>
                <Button
                    className="mrt-true-labels-edit"
                    size="tiny"
                    variant="tertiary"
                    startIcon={<SparkIcon slug="edit" />}
                    aria-label="Edit true labels"
                    iconSlug="edit"
                    disableWhenReadOnly
                    onClick={() => setEditingLabelsKey(rowId)}
                />
            </div>
        );
    };

    // Add a filter option for invalid responses
    const handleValidityFilterChange = useCallback((value) => {
        setFilters({
            ...filters,
            validity: value
        });
        setPagination({
            ...pagination,
            current: 1
        });
    }, [filters, pagination, setFilters, setPagination]);

    // Modify the handleTestResponseFilterChange function
    const handleTestResponseFilterChange = useCallback((value) => {
        // When clearing the filter (null/undefined or "All Labels"), remove testResponse from filters
        if (!value || value === NONE_SELECTED || value === ALL_LABELS) {
            const { testResponse, ...remainingFilters } = filters;
            setFilters(remainingFilters);
        } else {
            setFilters({
                ...filters,
                testResponse: value
            });
        }

        // Reset to first page when changing filters
        setPagination({
            ...pagination,
            current: 1
        });
    }, [filters, pagination, setFilters, setPagination]);

    // Determine the appropriate field names based on comparison type
    const comparisonField = isVersionComparison ? 'comparison_model_response' : 'teacher_response';

    const comparisonFilterKey = isVersionComparison ? 'comparisonModel' : 'oldModel';

    // Comparison model icon (SpecificAI logo for version comparisons, brain emoji for teacher)
    const comparisonModelIcon = isVersionComparison ? <SpecificAILogo /> : <span style={{ marginRight: '4px' }}>🧠</span>;

    const specificAiVersionName = parseInt(version) === version ? `SpecificAI v${version}.0` : `SpecificAI v${version}`;


    // Update the filter change handlers to reset isInitialLoad
    const handleFilterChange = (filterKey, value) => {
        isInitialLoad.current = false; // User manually changed filter
        setFilters(prev => ({
            ...prev,
            [filterKey]: value
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    // DropdownMenu displays `value` (the selected item's label) in its trigger.
    const hitMissFilterLabels = { hit: 'Correct', miss: 'Incorrect' };

    // Shared items for the Correct/Incorrect column filters; "All" clears the filter.
    const HIT_MISS_ALL = 'all';
    const hitMissFilterItems = [
        { id: HIT_MISS_ALL, label: 'All' },
        { id: 'hit', label: 'Correct' },
        { id: 'miss', label: 'Incorrect' },
    ];

    const columns = [
        {
            title: (
                <div className="model-responses-example-header">
                    <span>Example</span>
                    <Search
                        size="small"
                        placeholder="Search examples"
                        value={filters.example}
                        onChange={handleExampleFilterChange}
                        showClearButton={!!filters.example}
                        onClear={() => handleExampleFilterChange({ target: { value: '' } })}
                        className="model-responses-example-search"
                    />
                </div>
            ),
            dataIndex: 'request_example',
            key: 'example',
            flex: 1,
            minWidth: 200,
            render: (text) => {
                // Handle both string and array inputs
                const examples = Array.isArray(text) ? text : [text];
                return (
                    <div className="optune-scrollbar" style={{ maxHeight: 200, overflowY: 'auto', overflowX: 'hidden' }}>
                        <Text variant="base-regular" style={{ marginBottom: 0 }}>
                            {examples.join(', ')}
                        </Text>
                    </div>
                );
            },
        },
        // Only include comparison model column if comparisonModelName is not empty
        ...(comparisonModelName ? [{
            title: (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, width: '100%' }}>
                    <HeaderModelName icon={comparisonModelIcon} name={comparisonModelName} />
                    <div style={{ width: 140, flexShrink: 0 }}>
                        <DropdownMenu
                            items={hitMissFilterItems}
                            value={hitMissFilterLabels[filters[comparisonFilterKey]] || 'All'}
                            onSelect={(item) => handleFilterChange(comparisonFilterKey, item.id === HIT_MISS_ALL ? null : item.id)}
                            placeholder="All"
                            disabled={teacherModelProcessing && !isVersionComparison}
                            size="small"
                            showLabel={false}
                            panelLayout="overlay"
                        />
                    </div>
                    <TooltipTrigger text="Filter by label validity">
                        <Button
                            startIcon={<SparkIcon slug="circle-alert" />}
                            size="small"
                            variant={filters.validity === 'invalid' ? 'primary' : 'secondary'}
                            onClick={() => {
                                handleValidityFilterChange(filters.validity === 'invalid' ? null : 'invalid');
                            }}
                            disabled={teacherModelProcessing && !isVersionComparison}
                        />
                    </TooltipTrigger>
                </div>
            ),
            dataIndex: comparisonField,
            key: comparisonFilterKey,
            render: (value, record) => (
                <div className="mrt-cell-top">{renderComparisonResponse(value, record)}</div>
            ),
            width: 250,
        }] : []),
        {
            title: (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, width: '100%' }}>
                    <HeaderModelName icon={<SpecificAILogo />} name={specificAiVersionName} />
                    <div style={{ width: 140, flexShrink: 0 }}>
                        <DropdownMenu
                            items={hitMissFilterItems}
                            value={hitMissFilterLabels[filters.newModel] || 'All'}
                            onSelect={(item) => handleFilterChange('newModel', item.id === HIT_MISS_ALL ? null : item.id)}
                            placeholder="All"
                            disabled={studentModelProcessing}
                            size="small"
                            showLabel={false}
                            panelLayout="overlay"
                        />
                    </div>
                </div>
            ),
            dataIndex: 'new_model_response',
            key: 'newModel',
            render: (value, record) => (
                <div className="mrt-cell-top">{renderCurrentModelResponse(value, record)}</div>
            ),
            width: 310,
        },
        {
            title: (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, width: '100%' }}>
                    <span style={{ whiteSpace: 'nowrap' }}>True Labels</span>
                    <div style={{ flex: 1, minWidth: 120 }}>
                        <DropdownMenu
                            items={[
                                { id: ALL_LABELS, label: 'All Labels' },
                                ...uniqueLabels.map(label => ({
                                    id: label,
                                    label: label,
                                })),
                            ]}
                            value={filters.testResponse || 'All Labels'}
                            onSelect={(item) => handleTestResponseFilterChange(item.id)}
                            placeholder="All Labels"
                            size="small"
                            showLabel={false}
                            panelLayout="overlay"
                        />
                    </div>
                </div>
            ),
            dataIndex: 'test_response',
            key: 'testResponse',
            render: (text, record) => renderTestResponse(text, record, hasBenchmark),
            width: 250,
        },
        {
            title: <span style={{ whiteSpace: 'nowrap' }}>Review</span>,
            key: 'review',
            width: 150,
            render: (_, record) => (
                <ReviewStatusIndicator
                    record={record}
                    client_id={currentUsecase?.client_id}
                    currentUsecase={currentUsecase}
                    firstDatasetOrBenchmark={hasBenchmark ? evaluationBenchmarks?.[0] : evaluationDatasets?.[0]}
                    isBenchmark={hasBenchmark}
                    llm_usecase_id={llmUsecaseId}
                    updateRecord={updateRecord}
                    model_responses_id={record.id}
                    test_response={record.test_response}
                />
            ),
        },
    ];

    // Calculate pagination information
    const currentPage = pagination.current;
    const pageSize = pagination.pageSize;
    const totalItems = pagination.total;
    const totalPages = Math.ceil(totalItems / pageSize);
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    // Handle export
    const handleExport = () => {
        // Create a filters object that only includes non-null/empty values
        const apiFilters = Object.entries(filters).reduce((acc, [key, value]) => {
            if (value !== null && value !== '') {
                // Special handling for testResponse filter
                if (key === 'testResponse') {
                    acc[key] = {
                        label: value,
                        is_valid_label: true
                    };
                } else {
                    acc[key] = value;
                }
            }
            return acc;
        }, {});

        const requestData = {
            client_id: currentUsecase?.client_id,
            llm_usecase_id: llmUsecaseId,
            version: version,
            filters: apiFilters,
            comparison_model_name: comparisonModelName,
            selected_distillation_event_id: selectedDistillationEventId || null,
        };

        // Add comparison version if comparing with another version
        if (isVersionComparison) {
            requestData.comparison_version = comparisonVersion;
            requestData.is_version_comparison = true;
        }

        // Generate filename
        let filename = `model_responses_v${version}`;
        if (isVersionComparison) {
            filename += `_vs_v${comparisonVersion}`;
        } else {
            filename += `_vs_teacher`;
        }
        filename += '.json';

        exportData(
            "download-model-responses",
            requestData,
            filename,
            () => {
                sparkToast.success(
                    'Model responses have been exported successfully.'
                );
            },
            (error) => { sparkToast.error(error); }
        );
    };

    const isNer = taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS;
    const useEntityCollapsibleWrapper = isNer && entityExtractionsMode === EE_RENDER_MODES.ALL;

    const entityTable = isNer ? (
        <EntityExtractionsTable
            hideHeader
            mode={entityExtractionsMode}
            displayNames={displayNames}
            llmUsecaseId={llmUsecaseId}
            version={version}
            currentUsecase={currentUsecase}
            teacherModelProcessing={teacherModelProcessing}
            teacherModelFailed={teacherModelFailed}
            teacherModelErrorDetails={teacherModelErrorDetails}
            studentModelProcessing={studentModelProcessing}
            dropdownLabelRender={dropdownLabelRender}
            compareWith={compareWith}
            comparisonModelName={comparisonModelName}
            hasBenchmark={evaluationBenchmarks.length > 0}
            evaluationDatasets={evaluationDatasets}
            evaluationBenchmarks={evaluationBenchmarks}
            selectedLabel={selectedLabel}
            setLoading={setLoading}
            setData={setData}
            setPagination={setPagination}
            setFilters={setFilters}
            filters={filters}
            pagination={pagination}
            isInitialLoad={isInitialLoad}
            filterAttempt={filterAttempt}
            comparisonFilterKey={comparisonFilterKey}
            isVersionComparison={isVersionComparison}
            comparisonVersion={comparisonVersion}
            data={data}
            sampleCounters={sampleCounters}
            onTableChange={handleTableChange}
            allEntities={allEntities}
            activeTab={activeTab}
            loading={loading}
            selectedDistillationEventId={selectedDistillationEventId}
        />
    ) : null;

    return (
        <>
            {isNer ? (
                useEntityCollapsibleWrapper ? (
                    <CardDefault
                        type="collapsed"
                        open={false}
                        title={activeTab === 'relations' ? 'Relation Extractions' : 'Entity Extractions'}
                        subtitle={`Review and analyze ${activeTab === 'relations' ? 'relations' : 'entities'} extracted by the SpecificAI model and comparison models.`}
                        showSubtitle={true}
                        border="yes"
                    >
                        {entityTable}
                    </CardDefault>
                ) : (
                    entityTable
                )
            ) : (
                <>
                    {(teacherModelProcessing || teacherModelFailed || studentModelProcessing) && (
                        <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {teacherModelProcessing && !isVersionComparison && (
                                <TagFeedback label={`${comparisonModelName} Results Processing`} variant="info" showIcon />
                            )}
                            {teacherModelFailed && !isVersionComparison && (
                                <TagFeedback label={`${comparisonModelName} Teacher evaluation failed`} variant="error" showIcon />
                            )}
                            {studentModelProcessing && (
                                <TagFeedback label={`${specificAiVersionName} Results Processing`} variant="info" showIcon />
                            )}
                        </div>
                    )}
                    {teacherModelFailed && !isVersionComparison && !hideTeacherFailedAlert && (
                        <div style={{ marginBottom: 16 }}>
                            <ErrorDetailsAlert
                                title="Teacher evaluation failed"
                                message="Teacher evaluation failed. Teacher-side comparisons may be missing or stale."
                                details={teacherModelErrorDetails}
                            />
                        </div>
                    )}
                    <style>{`
                        .model-responses-table-wrap .spark-datatable-header-cell { height: auto; min-height: 42px; overflow: hidden; }
                        .model-responses-table-wrap .spark-datatable-data-cell { align-items: center; }
                    `}</style>
                    <div className="model-responses-table-wrap" style={{ marginTop: 16 }}>
                        <DataTable
                            columns={columns}
                            dataSource={data}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                onChange: (page) => handleTableChange({ current: page, pageSize: pagination.pageSize }),
                            }}
                            paginationExtra={
                                <Button
                                    className='fs-unmask'
                                    onClick={handleExport}
                                    size="small"
                                    variant="secondary"
                                    startIcon={<SparkIcon slug="download" />}
                                    disabled={loading || totalItems === 0}
                                >
                                    Export
                                </Button>
                            }
                            loading={loading}
                            rowKey={(record) => record.request_example}
                        />
                    </div>
                    {totalItems > 0 && (
                        <div className='fs-unmask' style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ display: 'inline-flex', color: 'var(--semantic-feedback-warning-on-surface)' }}>
                                    <SparkIcon slug="circle-alert-triangle" size="small" />
                                </span>
                                <Text variant="base-regular" color="secondary">
                                    <b>Invalid Label</b>: Indicates a response that doesn't match any of the defined labels, and is addressed as wrongly labeled.
                                </Text>
                            </div>
                            <Text variant="base-regular" color="secondary">
                                Showing {startItem}-{endItem} of {totalItems} items (Page {currentPage} of {totalPages})
                            </Text>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default ModelResponsesTable; 
